import React, { useEffect, useMemo, useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import AnimatedGlow, { type GlowEvent, type PresetConfig } from 'react-native-animated-glow';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '../hooks/useTheme';

interface Props {
  active: boolean;
}

// Apple Intelligence–style ring in Eleplan colours: brand blue flowing into
// violet, pink and the brand orange (keep brand stops in sync with
// theme/index.ts). Few, wide colour regions so the eye can follow them round.
const RING_COLORS = ['#00D9FF', '#9B7BFF', '#FF6FB5', '#EE6C4C'];
const RING_COLORS_SHIFTED = ['#FF6FB5', '#EE6C4C', '#00D9FF', '#9B7BFF'];
const RING_HIGHLIGHTS = ['#E8FBFF', '#FFE6F2', '#FFF0E0', '#E6ECFF'];

// The library has three states; they map to the ring's lifecycle:
//   press   — hidden (mount point for the intro, target of the outro)
//   hover   — the intro burst: thick, bright, fast-swirling
//   default — the steady ring it settles into
const BURST_MS = 520;
const OUTRO_MS = 360;

const getRingPreset = (isDarkMode: boolean, cornerRadius: number): PresetConfig => ({
  metadata: {
    name: 'AI Glow Ring',
    textColor: '#ffffff',
    category: 'Custom',
    tags: ['ai', 'brand', 'ring'],
  },
  states: [
    {
      name: 'default',
      preset: {
        cornerRadius,
        // The ring fills the screen, so there is nothing to draw outside it;
        // skip the library's default 100pt padding (patches/react-native-animated-glow).
        canvasMargin: 0,
        outlineWidth: 2.5,
        borderColor: RING_COLORS,
        backgroundColor: 'transparent',
        // ~5s per lap for the blobs.
        animationSpeed: 1.2,
        borderSpeedMultiplier: 0.8,
        // All layers sit inside the screen edge (the outside is off-screen).
        // A dim base band carries colour round all four edges; two thicker,
        // brighter blobs half a lap apart are what the eye follows, so the
        // ring reads as travelling rather than changing hue in place. Every
        // band moves the same way; opposite directions cancel into a shimmer.
        glowLayers: [
          {
            glowPlacement: 'inside',
            colors: RING_COLORS,
            glowSize: 12,
            opacity: isDarkMode ? 0.35 : 0.28,
            speedMultiplier: 0.7,
            coverage: 1,
            relativeOffset: 0,
          },
          {
            glowPlacement: 'inside',
            colors: RING_COLORS,
            glowSize: [2, 16, 16, 2],
            opacity: isDarkMode ? 0.55 : 0.45,
            speedMultiplier: 1,
            coverage: 0.45,
            relativeOffset: 0,
          },
          {
            glowPlacement: 'inside',
            colors: RING_COLORS_SHIFTED,
            glowSize: [2, 12, 12, 2],
            opacity: isDarkMode ? 0.45 : 0.38,
            speedMultiplier: 1,
            coverage: 0.4,
            relativeOffset: 0.5,
          },
          {
            glowPlacement: 'inside',
            colors: RING_HIGHLIGHTS,
            glowSize: 3,
            opacity: isDarkMode ? 0.5 : 0.32,
            speedMultiplier: 0.95,
            coverage: 1,
            relativeOffset: 0.6,
          },
      ],
      },
    },
    {
      name: 'hover',
      transition: BURST_MS,
      preset: {
        outlineWidth: 4,
        animationSpeed: 3,
        glowLayers: [
          { glowSize: 24, opacity: isDarkMode ? 0.6 : 0.5 },
          { glowSize: [4, 26, 26, 4], opacity: isDarkMode ? 0.8 : 0.65 },
          { glowSize: [4, 20, 20, 4], opacity: isDarkMode ? 0.7 : 0.6 },
          { glowSize: 5, opacity: isDarkMode ? 0.7 : 0.45 },
        ],
      },
    },
    {
      name: 'press',
      transition: OUTRO_MS,
      preset: {
        outlineWidth: 0,
        glowLayers: [
          { glowSize: 0, opacity: 0 },
          { glowSize: [0, 0, 0, 0], opacity: 0 },
          { glowSize: [0, 0, 0, 0], opacity: 0 },
          { glowSize: 0, opacity: 0 },
        ],
      },
    },
  ],
});

export default function AIGlowBorder({ active }: Props) {
  const glowSupported = Platform.OS === 'ios' || Platform.OS === 'android';
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const [visible, setVisible] = useState(false);
  const [glowState, setGlowState] = useState<GlowEvent>('press');
  // Face ID iPhones have ~55pt display corners; home-button iPhones are square.
  // Android corner radii vary by device, so the ring stays square there and
  // the display's own rounded corners clip it.
  const cornerRadius = Platform.OS === 'ios' && insets.bottom > 0 ? 55 : 0;
  const preset = useMemo(() => getRingPreset(theme.dark, cornerRadius), [theme.dark, cornerRadius]);

  // One-shot lifecycle: hidden → burst → settle on activation, → hidden on
  // deactivation. Each step is a single state change; the library animates
  // the transition on the UI thread, so nothing ticks on the JS thread while
  // the ring is showing.
  useEffect(() => {
    if (!glowSupported) return;
    if (active) {
      setVisible(true);
      setGlowState('press');
      const burstTimer = setTimeout(() => setGlowState('hover'), 16);
      const settleTimer = setTimeout(() => setGlowState('default'), 16 + BURST_MS);
      return () => {
        clearTimeout(burstTimer);
        clearTimeout(settleTimer);
      };
    }
    setGlowState('press');
    const hideTimer = setTimeout(() => setVisible(false), OUTRO_MS + 40);
    return () => clearTimeout(hideTimer);
  }, [active, glowSupported]);

  // Rendered as a plain absolute View — NOT a Modal — so that pointerEvents="none"
  // is fully reliable on iOS. Using a Modal (even with pointerEvents="none" on its
  // content) creates a second UIWindow on iOS that can intercept touches on newer
  // hardware / iOS 18, blocking controls underneath. A regular absolute View with
  // pointerEvents="none" is guaranteed to pass all touches through.
  //
  // The parent already fills the screen, so the ring's frame is the screen
  // itself. (It used to bleed outward by the safe-area insets, which pushed
  // the top and bottom edges off-screen.)
  if (!glowSupported || !visible) return null;
  return (
    <View style={styles.fill} pointerEvents="none">
      <AnimatedGlow
        preset={preset}
        style={styles.fill}
        initialState="press"
        activeState={glowState}
      >
        <View style={styles.fill} />
      </AnimatedGlow>
    </View>
  );
}

const styles = StyleSheet.create({
  fill: {
    ...StyleSheet.absoluteFillObject,
  },
});
